import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatMoney } from '@/lib/api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts'
import {
  BarChart3, FileText, FileSpreadsheet, RefreshCw, Search, X, Trophy,
  Users, Package, CreditCard, Layers, UserCircle, Store, Calendar, CalendarDays,
  DollarSign, ShoppingBag, Receipt,
} from 'lucide-react'

const PRESETS = [
  { v: 'hoje', l: 'Hoje' }, { v: 'mes', l: 'Mês' }, { v: 'mes_anterior', l: 'Mês anterior' },
  { v: 'ult30', l: '30 dias' }, { v: 'ult90', l: '90 dias' }, { v: 'personalizado', l: 'Personalizado' },
]
const DIMENSOES = [
  { v: 'vendedor', l: 'Vendedor', icon: Users }, { v: 'produto', l: 'Produto / Serviço', icon: Package },
  { v: 'forma', l: 'Forma de pgto', icon: CreditCard }, { v: 'grupo', l: 'Grupo', icon: Layers },
  { v: 'cliente', l: 'Cliente', icon: UserCircle }, { v: 'canal', l: 'Canal', icon: Store },
  { v: 'dia', l: 'Por dia', icon: Calendar }, { v: 'mes', l: 'Por mês', icon: CalendarDays },
]
const FORMAS = [
  ['dinheiro', 'Dinheiro'], ['pix', 'Pix'], ['cartao_credito', 'Cartão Crédito'],
  ['cartao_debito', 'Cartão Débito'], ['prazo', 'A Prazo'], ['boleto', 'Boleto'],
  ['credito_cliente', 'Crédito Cliente'], ['recebimento_bike', 'Recebimento Bike'],
  ['cheque', 'Cheque'], ['a_vista', 'À Vista'],
]
const CANAIS = [['', 'Todos'], ['balcao', 'PDV / Balcão'], ['digitado', 'Digitado'], ['mercado_livre', 'Mercado Livre']]
const TIPOS = [['', 'Tudo'], ['produto', 'Produtos'], ['servico', 'Serviços']]

function fmtCell(v: any, tipo: string) {
  if (v === null || v === undefined || v === '') return tipo === 'texto' ? '' : '—'
  if (tipo === 'moeda') return formatMoney(v)
  if (tipo === 'numero') return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (tipo === 'inteiro') return Number(v).toLocaleString('pt-BR')
  return String(v)
}
const isNum = (t: string) => t === 'moeda' || t === 'numero' || t === 'inteiro'
const norm = (s: any) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
const rotulo = (l: any) => l.dim ?? l.descricao ?? l.grupo ?? l.marca ?? l.codigo ?? '—'

// Botão-pill reutilizável
function Pill({ active, onClick, children }: any) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${active
        ? 'bg-violet-600 border-violet-600 text-white shadow-sm'
        : 'bg-white border-ink-200 text-ink-600 hover:border-violet-300 hover:text-violet-700'}`}>
      {children}
    </button>
  )
}

export default function AnaliseVendasPage() {
  const [preset, setPreset] = useState('mes')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [dimensao, setDimensao] = useState('vendedor')
  const [vendSel, setVendSel] = useState<number[]>([])
  const [forma, setForma] = useState('')
  const [canal, setCanal] = useState('')
  const [tipoItem, setTipoItem] = useState('')
  const [busca, setBusca] = useState('')
  const [baixando, setBaixando] = useState('')
  const [sort, setSort] = useState<{ k: string; d: 1 | -1 }>({ k: 'total', d: -1 })

  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['analise-vendedores'],
    queryFn: async () => (await api.get('/relatorios-gerenciais/vendas/vendedores')).data,
  })

  function paramsObj(): Record<string, string> {
    const p: Record<string, string> = { dimensao }
    if (preset === 'personalizado') { if (de) p.de = de; if (ate) p.ate = ate }
    else p.periodo = preset
    if (vendSel.length) p.vendedor_ids = vendSel.join(',')
    if (forma) p.forma = forma
    if (canal) p.canal = canal
    if (tipoItem) p.tipo_item = tipoItem
    return p
  }
  const qs = new URLSearchParams(paramsObj()).toString()
  const { data, isFetching, refetch } = useQuery<any>({
    queryKey: ['analise-vendas', qs],
    queryFn: async () => (await api.get(`/relatorios-gerenciais/vendas/analise?${qs}`)).data,
  })

  async function baixar(formato: 'pdf' | 'csv') {
    setBaixando(formato)
    try {
      const r = await api.get(`/relatorios-gerenciais/vendas/analise?${qs}&formato=${formato}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: formato === 'pdf' ? 'application/pdf' : 'text/csv' }))
      if (formato === 'pdf') window.open(url, '_blank')
      else { const a = document.createElement('a'); a.href = url; a.download = `analise-vendas-${dimensao}.csv`; a.click() }
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch { alert('Falha ao gerar o arquivo.') }
    finally { setBaixando('') }
  }

  const toggleVend = (id: number) => setVendSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])
  const limpar = () => { setVendSel([]); setForma(''); setCanal(''); setTipoItem('') }
  const filtrosAtivos = (vendSel.length ? 1 : 0) + (forma ? 1 : 0) + (canal ? 1 : 0) + (tipoItem ? 1 : 0)

  const colunas = data?.colunas || []
  const todas: any[] = data?.linhas || []
  const totais = data?.totais || {}
  const totalGeral = Number(totais.total || 0)
  const qtdGeral = Number(totais.qtd || 0)
  const ticket = qtdGeral ? totalGeral / qtdGeral : 0
  const isItemDim = ['produto', 'grupo', 'marca'].includes(dimensao)
  const qtdLabel = isItemDim ? 'Itens vendidos' : 'Pedidos'

  const b = busca.trim()
  let linhas = b ? todas.filter(l => colunas.some((c: any) => norm(l[c.key]).includes(norm(b)))) : todas
  linhas = [...linhas].sort((a, z) => {
    const av = a[sort.k], zv = z[sort.k]
    if (typeof av === 'number' || typeof zv === 'number') return ((Number(av) || 0) - (Number(zv) || 0)) * sort.d
    return String(av ?? '').localeCompare(String(zv ?? '')) * sort.d
  })
  const top = linhas[0]
  const topShare = top && totalGeral ? (Number(top.total || 0) / totalGeral) * 100 : 0
  const grafico = linhas.slice(0, 12).map((l, i) => ({ nome: String(rotulo(l)).slice(0, 16), total: Number(l.total || 0), i }))
  const dimLabel = DIMENSOES.find(d => d.v === dimensao)?.l || ''

  const sortBy = (k: string) => setSort(s => s.k === k ? { k, d: (s.d * -1) as 1 | -1 } : { k, d: -1 })

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-ink-900">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white"><BarChart3 size={20} /></span>
            Vendas por Vendedor
          </h1>
          {data?.subtitulo && <p className="text-xs text-ink-500 mt-1 ml-11">{data.subtitulo}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => baixar('pdf')} disabled={!!baixando || !linhas.length}
            className="px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 border border-ink-200 hover:bg-ink-50 disabled:opacity-40">
            <FileText size={15} /> {baixando === 'pdf' ? '...' : 'PDF'}
          </button>
          <button onClick={() => baixar('csv')} disabled={!!baixando || !linhas.length}
            className="px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 border border-ink-200 hover:bg-ink-50 disabled:opacity-40">
            <FileSpreadsheet size={15} /> {baixando === 'csv' ? '...' : 'Excel'}
          </button>
        </div>
      </div>

      {/* Painel de filtros */}
      <div className="bg-white rounded-2xl border border-ink-100 shadow-sm p-4 space-y-4">
        {/* Período */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-2">Período</div>
          <div className="flex items-center gap-2 flex-wrap">
            {PRESETS.map(p => <Pill key={p.v} active={preset === p.v} onClick={() => setPreset(p.v)}>{p.l}</Pill>)}
            {preset === 'personalizado' && (
              <span className="flex items-center gap-2 ml-1">
                <input type="date" className="input !py-1.5 text-xs" value={de} onChange={e => setDe(e.target.value)} />
                <span className="text-ink-400 text-xs">até</span>
                <input type="date" className="input !py-1.5 text-xs" value={ate} onChange={e => setAte(e.target.value)} />
              </span>
            )}
          </div>
        </div>

        {/* Agrupar por (abas com ícone) */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-2">Agrupar por</div>
          <div className="flex items-center gap-2 flex-wrap">
            {DIMENSOES.map(d => {
              const Icon = d.icon
              const on = dimensao === d.v
              return (
                <button key={d.v} onClick={() => setDimensao(d.v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border transition-all ${on
                    ? 'bg-ink-900 border-ink-900 text-white shadow'
                    : 'bg-white border-ink-200 text-ink-600 hover:border-ink-400'}`}>
                  <Icon size={14} /> {d.l}
                </button>
              )
            })}
          </div>
        </div>

        {/* Filtros */}
        <div className="border-t border-ink-100 pt-3 space-y-3">
          {/* Vendedor (chips multi) */}
          {vendedores.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-2 flex items-center gap-1"><Users size={12} /> Vendedor {vendSel.length > 0 && <span className="text-violet-600">({vendSel.length})</span>}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <Pill active={vendSel.length === 0} onClick={() => setVendSel([])}>Todos</Pill>
                {vendedores.map((v: any) => (
                  <Pill key={v.id} active={vendSel.includes(v.id)} onClick={() => toggleVend(v.id)}>
                    {v.nome} <span className="opacity-60">· {formatMoney(v.total)}</span>
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {/* Forma + Canal + Tipo */}
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1.5">Forma de pagamento</div>
              <select className="input !py-1.5 text-sm" value={forma} onChange={e => setForma(e.target.value)}>
                <option value="">Todas</option>
                {FORMAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1.5">Canal</div>
              <div className="flex gap-1.5">{CANAIS.map(([v, l]) => <Pill key={v} active={canal === v} onClick={() => setCanal(v)}>{l}</Pill>)}</div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1.5">Tipo</div>
              <div className="flex gap-1.5">{TIPOS.map(([v, l]) => <Pill key={v} active={tipoItem === v} onClick={() => setTipoItem(v)}>{l}</Pill>)}</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {filtrosAtivos > 0 && <button onClick={limpar} className="text-xs text-ink-500 hover:text-rose-600 flex items-center gap-1"><X size={13} /> Limpar ({filtrosAtivos})</button>}
              <button onClick={() => refetch()} className="px-3 py-2 rounded-lg text-sm bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-1.5">
                <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Atualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { lbl: 'Total vendido', val: formatMoney(totalGeral), icon: DollarSign, grad: 'from-emerald-500 to-teal-500' },
          { lbl: qtdLabel, val: qtdGeral.toLocaleString('pt-BR'), icon: ShoppingBag, grad: 'from-blue-500 to-indigo-500' },
          { lbl: 'Ticket médio', val: formatMoney(ticket), icon: Receipt, grad: 'from-violet-500 to-fuchsia-500' },
        ].map((k, i) => {
          const Icon = k.icon
          return (
            <div key={i} className="bg-white rounded-2xl border border-ink-100 shadow-sm p-4 flex items-center gap-3">
              <span className={`w-11 h-11 rounded-xl bg-gradient-to-br ${k.grad} flex items-center justify-center text-white shrink-0`}><Icon size={20} /></span>
              <div className="min-w-0">
                <div className="text-xs text-ink-500">{k.lbl}</div>
                <div className="text-xl font-bold text-ink-900 truncate">{k.val}</div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Insight */}
      {top && totalGeral > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm">
          <Trophy size={16} className="text-amber-500 shrink-0" />
          <span className="text-ink-700">
            <b>{rotulo(top)}</b> lidera por {dimLabel.toLowerCase()} — <b>{formatMoney(top.total)}</b>
            <span className="text-ink-500"> ({topShare.toFixed(1)}% do total)</span>
          </span>
        </div>
      )}

      {/* Busca */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-8 pr-8 w-full !py-1.5 text-sm" placeholder={`Buscar em ${dimLabel.toLowerCase()}...`} value={busca} onChange={e => setBusca(e.target.value)} />
          {busca && <button onClick={() => setBusca('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"><X size={14} /></button>}
        </div>
        <span className="text-xs text-ink-400">{linhas.length} linha(s)</span>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-ink-100 shadow-sm overflow-x-auto">
        {isFetching ? (
          <div className="p-10 text-center text-ink-400">Carregando…</div>
        ) : !linhas.length ? (
          <div className="p-10 text-center text-ink-400">Nenhuma venda nos filtros selecionados.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800 text-white">
                {colunas.map((c: any) => (
                  <th key={c.key} onClick={() => sortBy(c.key)}
                    className={`px-3 py-2.5 font-semibold cursor-pointer select-none hover:bg-slate-700 ${isNum(c.tipo) ? 'text-right' : 'text-left'}`}>
                    {c.label}{sort.k === c.key ? (sort.d === -1 ? ' ↓' : ' ↑') : ''}
                  </th>
                ))}
                {colunas.some((c: any) => c.key === 'total') && <th className="px-3 py-2.5 font-semibold text-right w-40">% Part.</th>}
              </tr>
            </thead>
            <tbody>
              {linhas.map((l, i) => {
                const share = totalGeral ? (Number(l.total || 0) / totalGeral) * 100 : 0
                return (
                  <tr key={i} className="border-b border-ink-50 hover:bg-violet-50/40">
                    {colunas.map((c: any) => (
                      <td key={c.key} className={`px-3 py-2 ${isNum(c.tipo) ? 'text-right tabular-nums' : 'text-left'} ${c.key === colunas[0].key ? 'font-medium text-ink-800' : 'text-ink-600'}`}>
                        {fmtCell(l[c.key], c.tipo)}
                      </td>
                    ))}
                    {colunas.some((c: any) => c.key === 'total') && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 justify-end">
                          <div className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden max-w-[80px]">
                            <div className="h-full bg-violet-500 rounded-full" style={{ width: `${Math.min(100, share)}%` }} />
                          </div>
                          <span className="text-xs text-ink-500 tabular-nums w-10 text-right">{share.toFixed(1)}%</span>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                {colunas.map((c: any, i: number) => (
                  <td key={c.key} className={`px-3 py-2.5 ${isNum(c.tipo) ? 'text-right tabular-nums' : 'text-left'}`}>
                    {c.key in totais ? fmtCell(totais[c.key], c.tipo) : (i === 0 ? 'TOTAL' : '')}
                  </td>
                ))}
                {colunas.some((c: any) => c.key === 'total') && <td className="px-3 py-2.5 text-right text-ink-500">100%</td>}
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Gráfico (por último) */}
      {grafico.length > 0 && (
        <div className="bg-white rounded-2xl border border-ink-100 shadow-sm p-4">
          <div className="text-sm font-semibold text-ink-700 mb-2">Top {grafico.length} por {dimLabel.toLowerCase()}</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={grafico} margin={{ left: 10, right: 10, top: 5, bottom: 55 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="nome" angle={-28} textAnchor="end" interval={0} height={64} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tickFormatter={v => formatMoney(v)} width={92} tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip formatter={(v: any) => formatMoney(v)} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="total" radius={[5, 5, 0, 0]}>
                {grafico.map((g, i) => <Cell key={i} fill={i === 0 ? '#7c3aed' : '#c4b5fd'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
